import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { 
    Container, 
    TextField, 
    Box, 
    Typography, 
    Button, 
    Alert, 
    Paper, 
    InputAdornment,
    IconButton,
    CssBaseline,
    Divider,
    CircularProgress
} from "@mui/material";
import { 
    Lock as LockIcon, 
    Visibility,
    VisibilityOff,
    ArrowForward,
    ArrowBack,
    CheckCircleOutline
} from "@mui/icons-material";

import bgImage from '../assets/background_test.png';
import authApi from "../authApi";

const ResetPassword = ({ navbar }) => {
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [validatingToken, setValidatingToken] = useState(true);
    const [tokenValid, setTokenValid] = useState(false);
    const { token } = useParams();
    const navigate = useNavigate();

    // Validate token on component mount
    useEffect(() => {
        const validateToken = async () => {
            if (!token) {
                setTokenValid(false);
                setValidatingToken(false);
                return;
            }

            try {
                const { data } = await authApi.email.get(`/validate-reset-token/${token}`);
                setTokenValid(data.valid);
            } catch (error) {
                setTokenValid(false);
            } finally {
                setValidatingToken(false);
            }
        };

        validateToken();
    }, [token]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        
        // Password validation
        if (password !== confirmPassword) {
            setError("Passwords do not match.");
            return;
        }

        if (password.length < 8) {
            setError("Password must be at least 8 characters long.");
            return;
        }

        setIsLoading(true);

        try {
            const { data } = await authApi.email.post("/reset-password", { 
                token,
                password
            });
            setSuccess(true);
            setPassword("");
            setConfirmPassword("");
        } catch (error) {
            const message = error.response?.data?.message || "Failed to reset password. Please try again.";
            setError(message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleClickShowPassword = () => {
        setShowPassword(!showPassword);
    };

    const handleClickShowConfirmPassword = () => {
        setShowConfirmPassword(!showConfirmPassword);
    };

    const handleMouseDownPassword = (event) => {
        event.preventDefault();
    };

    const handleBackToLogin = () => {
        navigate('/login');
    };

    // Quote for the right side (same as Login component)
    const quote = {
        text: "The brain craves joy. Feed it learning wrapped in play."
    };

    // If still validating token
    if (validatingToken) {
        return (
            <Box 
                sx={{
                    position: 'relative',
                    width: '100%',
                    minHeight: 'calc(100vh - 64px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: `linear-gradient(rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.2)), url(${bgImage}) center center / 100% no-repeat`,
                }}
            >
                {/* {navbar} */}
                <CssBaseline />
                <Container maxWidth="sm">
                    <Paper 
                        elevation={5} 
                        sx={{ 
                            p: 4, 
                            display: 'flex', 
                            flexDirection: 'column', 
                            alignItems: 'center',
                            borderRadius: 3,
                        }}
                    >
                        <CircularProgress color="primary" sx={{ mb: 2 }} />
                        <Typography variant="h6">Validating your reset link...</Typography>
                    </Paper>
                </Container>
            </Box>
        );
    }

    // If token is invalid
    if (!tokenValid && !validatingToken) {
        return (
            <Box 
                sx={{
                    position: 'relative',
                    width: '100%',
                    minHeight: 'calc(100vh - 64px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: `linear-gradient(rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.2)), url(${bgImage}) center center / 100% no-repeat`,
                }}
            >
                {navbar}
                <CssBaseline />
                <Container maxWidth="sm">
                    <Paper 
                        elevation={5} 
                        sx={{ 
                            p: 4, 
                            display: 'flex', 
                            flexDirection: 'column', 
                            alignItems: 'center',
                            borderRadius: 3,
                        }}
                    >
                        <Typography 
                            variant="h4" 
                            color="error" 
                            sx={{ 
                                mb: 2,
                                fontWeight: 700,
                            }}
                        >
                            Invalid or Expired Link
                        </Typography>
                        <Typography variant="body1" textAlign="center" sx={{ mb: 3 }}>
                            This password reset link is invalid or has expired. 
                            Please request a new password reset link.
                        </Typography>
                        <Button 
                            variant="contained" 
                            onClick={() => navigate('/forgot-password')}
                            sx={{ 
                                mt: 2, 
                                py: 1.5,
                                px: 3,
                                borderRadius: 2,
                            }}
                        >
                            Request New Link
                        </Button>
                        <Button
                            startIcon={<ArrowBack />}
                            onClick={handleBackToLogin}
                            sx={{ 
                                mt: 2,
                                color: 'text.secondary',
                                '&:hover': {
                                    color: 'primary.main',
                                    backgroundColor: 'transparent'
                                }
                            }}
                        >
                            Back to Login
                        </Button>
                    </Paper>
                </Container>
            </Box>
        );
    }

    // If password was successfully reset
    if (success) {
        return (
            <Box 
                sx={{
                    position: 'relative',
                    width: '100%',
                    minHeight: 'calc(100vh - 64px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: `linear-gradient(rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.2)), url(${bgImage}) center center / 100% no-repeat`,
                }}
            >
                {navbar}
                <CssBaseline />
                <Container maxWidth="sm">
                    <Paper 
                        elevation={5} 
                        sx={{ 
                            p: 4, 
                            display: 'flex', 
                            flexDirection: 'column', 
                            alignItems: 'center',
                            borderRadius: 3,
                        }}
                    >
                        <CheckCircleOutline 
                            color="success" 
                            sx={{ fontSize: 64, mb: 2 }} 
                        />
                        <Typography 
                            variant="h4" 
                            color="primary" 
                            sx={{ 
                                mb: 2,
                                fontWeight: 700,
                            }}
                        >
                            Password Reset Successful
                        </Typography>
                        <Typography variant="body1" textAlign="center" sx={{ mb: 3 }}>
                            Your password has been successfully reset. 
                            You can now log in with your new password.
                        </Typography>
                        <Button 
                            variant="contained" 
                            onClick={handleBackToLogin}
                            sx={{ 
                                mt: 2, 
                                py: 1.5,
                                px: 3,
                                borderRadius: 2,
                            }}
                        >
                            Go to Login
                        </Button>
                    </Paper>
                </Container>
            </Box>
        );
    }

    // Main reset password form
    return (
        <Box 
            sx={{
                position: 'relative',
                width: '100%',
                minHeight: 'calc(100vh - 64px)', // Subtract navbar height
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: `linear-gradient(rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.2)), url(${bgImage}) center center / 100% no-repeat`,
                overflow: 'auto',
                mt: 0
            }}
        >
            {navbar}

            <CssBaseline />

            <Container 
                maxWidth="md" 
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    py: 4
                }}
            >
                <Paper 
                    elevation={10} 
                    sx={{ 
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        borderRadius: 3,
                        width: '100%',
                        overflow: 'hidden',
                        backgroundColor: 'white',
                    }}
                >
                    {/* Reset Password Form Side */}
                    <Box
                        sx={{
                            padding: { xs: 3, sm: 4 },
                            display: 'flex', 
                            flexDirection: 'column',
                            width: { xs: '100%', md: '60%' },
                        }}
                    >
                        <Typography 
                            variant="h4" 
                            sx={{ 
                                mb: 2,
                                fontWeight: 700,
                                color: 'primary.main',
                            }}
                        >
                            Reset Your Password
                        </Typography>

                        <Typography 
                            variant="subtitle1" 
                            sx={{ 
                                mb: 4,
                                color: 'text.secondary'
                            }}
                        >
                            Enter your new password below
                        </Typography>

                        {error && (
                            <Alert 
                                severity="error" 
                                sx={{ 
                                    mb: 3, 
                                    width: '100%',
                                    borderRadius: 1
                                }}
                            >
                                {error}
                            </Alert>
                        )}

                        <form onSubmit={handleSubmit} style={{ width: '100%' }}>
                            <TextField
                                label="New Password"
                                type={showPassword ? "text" : "password"}
                                variant="outlined"
                                fullWidth
                                margin="normal"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <LockIcon color="primary" />
                                        </InputAdornment>
                                    ),
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle password visibility"
                                                onClick={handleClickShowPassword}
                                                onMouseDown={handleMouseDownPassword}
                                                edge="end"
                                            >
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{ mb: 3 }}
                            />
                            
                            <TextField
                                label="Confirm Password"
                                type={showConfirmPassword ? "text" : "password"}
                                variant="outlined"
                                fullWidth
                                margin="normal"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                required
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <LockIcon color="primary" />
                                        </InputAdornment>
                                    ),
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle password visibility"
                                                onClick={handleClickShowConfirmPassword}
                                                onMouseDown={handleMouseDownPassword}
                                                edge="end"
                                            >
                                                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{ mb: 3 }}
                            />
                            
                            <Button 
                                type="submit" 
                                variant="contained" 
                                fullWidth 
                                disabled={isLoading}
                                sx={{ 
                                    mt: 2, 
                                    py: 1.5,
                                    borderRadius: 2,
                                    transition: 'all 0.2s ease-in-out',
                                    '&:hover': {
                                        transform: 'translateY(-2px)',
                                        boxShadow: 4
                                    },
                                    boxShadow: 2
                                }}
                                endIcon={<ArrowForward />}
                            >
                                {isLoading ? "Resetting..." : "Reset Password"}
                            </Button>
                            
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                                <Button
                                    startIcon={<ArrowBack />}
                                    onClick={handleBackToLogin}
                                    sx={{ 
                                        color: 'text.secondary',
                                        '&:hover': {
                                            color: 'primary.main',
                                            backgroundColor: 'transparent'
                                        }
                                    }}
                                >
                                    Back to Login
                                </Button>
                            </Box>
                        </form>
                    </Box>

                    {/* Quote Side - same as Login component */}
                    <Box
                        sx={{
                            width: { xs: '100%', md: '40%' },
                            bgcolor: 'primary.main',
                            color: 'white',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            padding: 4,
                            position: 'relative'
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                maxWidth: '80%',
                                position: 'relative',
                                py: 4
                            }}
                        >
                            <Typography
                                variant="h2"
                                sx={{
                                    fontSize: '72px',
                                    fontWeight: 700,
                                    color: 'rgba(255,255,255,0.2)',
                                    position: 'absolute',
                                    top: -10,
                                    left: -20
                                }}
                            >
                                "
                            </Typography>
                            <Typography
                                variant="h6"
                                sx={{
                                    fontWeight: 500,
                                    fontStyle: 'italic',
                                    mb: 2,
                                    zIndex: 1
                                }}
                            >
                                {quote.text}
                            </Typography>
                            <Divider sx={{ bgcolor: 'rgba(255,255,255,0.3)', width: '60px', mb: 2 }} />
                            <Typography
                                variant="h2"
                                sx={{
                                    fontSize: '72px',
                                    fontWeight: 700,
                                    color: 'rgba(255,255,255,0.2)',
                                    position: 'absolute',
                                    bottom: -60,
                                    right: -20
                                }}
                            >
                                "
                            </Typography>
                        </Box>
                    </Box>
                </Paper>
            </Container>
        </Box>
    );
};

export default ResetPassword;